/* eslint-disable linebreak-style */

import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  EditOutlined,
  UnlockOutlined,
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, notification, Tooltip, Typography, Upload, Table } from 'antd';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { get } from 'lodash';
import debounce from 'lodash/debounce';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Users } from 'lucide-react';
import { ConfirmationModal } from '@crc-evans/ui-components';

import AddUserModal from '../../components/AddUserModal';
import {
  getUsers,
  getAccess,
  getAllUsersList,
  getAllUsersGroupsList,
} from '../../services/teamMembers';
import {
  saveUsers,
  removeAccess,
  resetUserPassword,
  addAccess,
  removeUser,
} from '../../services/userManagement';
import ResetPasswordModal from '../../components/ResetPasswordModal';
import '../technical/HeatProfilePage.css';

const { Title, Text } = Typography;

const UserManagementAGGrid = (props) => {
  const { } = props;

  // Search states
  const [nameSearch, setNameSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');

  const [filteredData, setFilteredData] = useState([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetPasswordModalLoading, setResetPasswordModalLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [userData, setUserData] = useState([]);
  const [showLoader, setShowLoader] = useState(true);
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [createUserLoader, setCreateUserLoader] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [userForm] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [userName, setUserName] = useState('');
  const [userGroup, setUserGroup] = useState([]);
  const [resetPasswordRecord, setResetPasswordRecord] = useState([]);
  const [userGroupsList, setUserGroupsList] = useState([]);
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  const getUsersList = async (pageToken, pageAction, searchKeyExist) => {
    setShowLoader(true);
    const response = await getAllUsersList();
    if (response && response.users.length) {
      setPageTokens([...pageTokens, response.paginationToken]);
      setUserCount(response.count);
      // eslint-disable-next-line consistent-return
      let usersForProject = response.users.map((obj) => {
        const details = { user_id: obj.Username };
        obj.Attributes.forEach((element) => {
          if (element.Name === 'custom:first_name') {
            details.userFirstName = element.Value;
          } else if (element.Name === 'custom:last_name') {
            details.userLastName = element.Value;
          } else if (element.Name === 'email') {
            details.email = element.Value;
          }
        });
        details.userLastName
          ? (details.name = details.userFirstName + ' ' + details?.userLastName)
          : (details.name = details.userFirstName);
        details.userStatus = obj.UserStatus;
        details.enabled = obj.Enabled;
        if (details.name && details.email && details.userStatus && details.enabled) {
          return details;
        }
      });
      usersForProject = usersForProject.filter((el) => el);
      usersForProject.sort((a, b) => a.name.localeCompare(b.name));
      setUserData(usersForProject);
      setFilteredData(usersForProject);
      setShowLoader(false);
    } else if (response.users.length == 0) {
      // notification.warning({
      //   message: 'User does not exist with the given email.',
      //   duration: 1.2
      // });
      console.log('User does not exist with the given email');
      setUserData(null);
      setShowLoader(false);
    } else {
      notification.error({
        message: 'Something went wrong.',
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getUsersList();

      if (userGroupsList.length === 0) {
        const response = await getAllUsersGroupsList();
        if (response) setUserGroupsList(response);
      }
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtering Logic
  useEffect(() => {
    let result = userData || [];

    if (nameSearch) {
      result = result.filter((item) =>
        item.name?.toLowerCase().includes(nameSearch.toLowerCase())
      );
    }
    if (emailSearch) {
      result = result.filter((item) =>
        item.email?.toLowerCase().includes(emailSearch.toLowerCase())
      );
    }
    if (statusSearch) {
      result = result.filter((item) =>
        item.userStatus?.toLowerCase().includes(statusSearch.toLowerCase())
      );
    }

    setFilteredData(result);
  }, [nameSearch, emailSearch, statusSearch, userData]);

  const debouncedSetNameSearch = useCallback(
    debounce((val) => setNameSearch(val), 300),
    []
  );

  const debouncedSetEmailSearch = useCallback(
    debounce((val) => setEmailSearch(val), 300),
    []
  );

  const debouncedSetStatusSearch = useCallback(
    debounce((val) => setStatusSearch(val), 300),
    []
  );

  const renderColumnTitle = (title, setter, width) => (
    <div className="column-header-with-filter">
      <div className="column-header-text-wrapper">
        <span>{title}</span>
      </div>
      <div className="header-filter-input">
        <Input
          placeholder="Search"
          onChange={(e) => setter(e.target.value)}
          allowClear
          onClick={(e) => e.stopPropagation()}
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: '12px' }} />}
          style={{ width: width || '100%' }}
        />
      </div>
    </div>
  );

  const showResetPasswordModal = (record) => {
    setResetPasswordRecord(record);
    setResetPasswordModalVisible(true);
  };

  const openDeleteUserModal = (record) => {
    setUserToDelete(record);
    setDeleteUserModalOpen(true);
  };

  const closeDeleteUserModal = () => {
    if (deleteUserLoading) return;
    setDeleteUserModalOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = () => {
    if (!userToDelete || deleteUserLoading) return;
    setDeleteUserLoading(true);
    removeUser(userToDelete.user_id, userToDelete.email)
      .then((res) => {
        if (res) {
          notification.success({
            message: 'User deleted successfully',
          });
          getUsersList();
          setDeleteUserModalOpen(false);
          setUserToDelete(null);
        } else {
          notification.error({
            message: 'Something went wrong.',
          });
        }
      })
      .catch(() =>
        notification.error({
          message: 'Something went wrong.',
        })
      )
      .finally(() => setDeleteUserLoading(false));
  };

  const saveUser = async (data, sendWelcomeEmail = true) => {
    const res = await saveUsers(data);

    // Check for success response: { "message": "success", "data": { "User": {...} } }
    if (res.status === 200 && (res.message === 'success' || res.data?.User)) {
      const userData = res.data?.User;
      const userStatus = userData?.UserStatus;

      // Check if user already exists (e.g., FORCE_CHANGE_PASSWORD status)
      const userExists = userStatus === 'FORCE_CHANGE_PASSWORD' || userStatus === 'CONFIRMED';

      if (userExists && !sendWelcomeEmail) {
        // User exists but we're not sending email - treat as success
        notification.success({
          message: 'User already exists.',
          description: `${data.email} is already in the system.`,
          duration: 3,
        });
      } else if (sendWelcomeEmail) {
        notification.success({
          message: 'User created successfully!',
          description: `An invitation email has been sent to ${data.email} with a link to set their password.`,
          duration: 4,
        });
      } else {
        notification.success({
          message: 'User created successfully!',
          description: `${data.email} has been added. No invitation email was sent.`,
          duration: 3,
        });
      }
      userForm.resetFields();
      setUserModalVisible(false);
      getUsersList();
    } else {
      // Handle error cases: { "error": "User with the given email already exists." }
      const errorMessage = res.error || 'User with the given email already exists.';
      notification.warning({
        message: errorMessage,
        duration: 1.2,
      });
    }
    setCreateUserLoader(false);
    setUserModalLoading(false);
  };

  const resetPassword = async (data) => {
    resetPasswordForm.resetFields();
    const response = await resetUserPassword(data);
    if (response) {
      notification.success({
        message: 'Password has been reset successfully',
      });
    } else {
      notification.success({
        message: 'Something went wrong',
      });
    }
    setResetPasswordModalLoading(false);
    setResetPasswordModalVisible(false);
  };

  const showAddEditUserModal = async (record, edit) => {
    console.log(record);
    let userDt = {};
    if (record !== '') {
      const response = await getAccess(record.user_id);
      const userAccess = response.map((item) => item?.GroupName);
      const fullName = record.name.split(' ');
      const fName = fullName[0];
      if (fullName.length === 2) {
        const lName = fullName[1];
        userDt = {
          email: record.email,
          firstName: fName,
          lastName: lName,
          access: userAccess,
        };
      } else {
        userDt = {
          email: record.email,
          firstName: fName,
          access: userAccess,
        };
      }
      userForm.setFieldsValue(userDt);
      setUserName(record.user_id);
      setUserGroup(userAccess);
    } else {
      userForm.resetFields();
    }

    setIsEdit(edit);

    setUserModalVisible(true);
  };

  const onAdduserToGroup = async (dataList) => {
    try {
      setUserModalLoading(true);
      const failedGroups = [];

      // Loop through all data and process them
      for (const data of dataList) {
        const response = await addAccess(data);
        if (response !== 200) {
          failedGroups.push(data); // Store failed attempts
        }
      }

      // Show notification based on success or failure
      if (failedGroups.length > 0) {
        notification.error({
          message: `Failed to add ${failedGroups.length} group(s).`,
          duration: 1.5,
        });
      } else {
        notification.success({
          message: 'User added to groups successfully',
          duration: 1.5,
        });
      }

      await getUsersList(); // Refresh user list
    } catch (error) {
      notification.error({
        message: 'Something went wrong.',
      });
    } finally {
      setUserModalLoading(false);
      setCreateUserLoader(false);
      setUserModalVisible(false);
    }
  };

  const onRemoveuserFromGroup = async (dataList) => {
    try {
      setUserModalLoading(true);
      const failedGroups = [];

      // Loop through all data and process them
      for (const data of dataList) {
        const response = await removeAccess(data);
        if (response !== 200) {
          failedGroups.push(data); // Store failed attempts
        }
      }

      // Show notification based on success or failure
      if (failedGroups.length > 0) {
        notification.error({
          message: `Failed to remove ${failedGroups.length} group(s).`,
          duration: 1.5,
        });
      } else {
        notification.success({
          message: 'User removed from groups successfully',
          duration: 1.5,
        });
      }

      await getUsersList(); // Refresh user list
    } catch (error) {
      notification.error({
        message: 'Something went wrong.',
      });
    } finally {
      setUserModalLoading(false);
      setCreateUserLoader(false);
      setUserModalVisible(false);
    }
  };

  const columns = [
    {
      title: renderColumnTitle('Name', debouncedSetNameSearch, '180px'),
      dataIndex: 'name',
      key: 'name',
      width: '250px',
    },
    {
      title: renderColumnTitle('Email', debouncedSetEmailSearch, '250px'),
      dataIndex: 'email',
      key: 'email',
      width: '350px',
    },
    {
      title: renderColumnTitle('Confirm Status', debouncedSetStatusSearch, '250px'),
      dataIndex: 'userStatus',
      key: 'userStatus',
      width: '280px',
      align: 'left',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '150px',
      align: 'center',
      fixed: 'right',
      render: (text, record) => (
        <div className="heat-profile-actions-cell" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <Tooltip title="Edit User Access">
            <EditOutlined
              className="txtIcon"
              style={{ color: '#00A86B', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showAddEditUserModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Reset User Password">
            <UnlockOutlined
              className="txtIcon"
              style={{ color: '#1677ff', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => showResetPasswordModal(record, true)}
            />
          </Tooltip>
          <Tooltip title="Delete User">
            <DeleteOutlined
              className="txtIcon"
              style={{ color: '#ef314c', fontSize: '18px', cursor: 'pointer' }}
              onClick={() => openDeleteUserModal(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const treeData = [
    {
      title: 'Super Admin',
      key: 'admin',
    },
    {
      title: 'Admin',
      key: 'super-admin',
    },
    {
      title: 'Projects',
      key: 'web-page',
    },
    {
      title: 'Analysis',
      key: 'analysis',
    },
    {
      title: 'Ask Evans',
      key: 'AskEvans',
      children: [
        {
          title: 'Admin',
          key: 'askevans-training-data',
        },
      ],
    },
    {
      title: 'Technical',
      key: 'technical',
      children: [
        {
          title: 'Heat Profile',
          key: 'heat-profile',
        },
        {
          title: 'Coating Record Database',
          key: 'coating-record-db',
        },
        {
          title: 'DCAL Generator',
          key: 'dcal-generators',
          children: [
            {
              title: 'Admin',
              key: 'dcal-generator-master',
            },
            {
              title: 'Normal User',
              key: 'dcal-generator',
            },
          ],
        },
      ],
    },
    {
      title: 'Calibration & Control',
      key: 'Calibration-Control',
      children: [
        {
          title: 'Normal user',
          key: 'calibration-normal-user',
        },
      ],
    },
  ];

  const handleDownloadData = async () => {
    setDownloadLoading(true);

    const treeColumns = [];

    const processTree = (nodes, parentTitle) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          processTree(node.children, node.title); // Recurse with parent title
        } else {
          const header = parentTitle ? `${parentTitle} - ${node.title}` : node.title;
          treeColumns.push({
            header,
            key: node.key,
            width: 40,
          });
        }
      });
    };

    // Process tree data to generate dynamic columns
    processTree(treeData);

    const workbook = new ExcelJS.Workbook();
    const databaseList = rowData;

    if (databaseList && databaseList.length > 0) {
      const sheet = workbook.addWorksheet('User List');
      const headerRow = sheet.getRow(1);
      headerRow.height = 20;

      const columns = [
        ...Object.keys({
          name: 'Name',
          email: 'Email',
          userStatus: 'Confirm Status',
        })
          .map((key) => ({
            header: {
              name: 'Name',
              email: 'Email',
              userStatus: 'Confirm Status',
            }[key],
            key,
            width: 40,
          })),
        ...treeColumns,
      ];

      sheet.columns = columns;

      const usersWithGroups = databaseList.map((user) => ({
        ...user,
        Groups: userGroupsList?.users[user.user_id] ?? [],
      }));
      // Ensure each row has all treeData keys and mark group memberships
      const filledData = usersWithGroups.map((user) => {
        const extendedRow = { ...user };

        // Add group flags based on treeColumns
        treeColumns.forEach((col) => {
          // If this group exists in user's group list, mark it
          extendedRow[col.key] = user.Groups.includes(col.key) ? '✔' : '';
        });

        return extendedRow;
      });

      sheet.addRows(filledData);
    }

    // Export Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `List of User ${moment().format('DD-MM-YYYY')}.xlsx`;
    link.click();

    setDownloadLoading(false);
  };

  return (
    <>
      <div className="heat-profile-content-wrapper">
        <div className="heat-profile-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div className="heat-profile-icon-wrap" style={{ backgroundColor: '#f3e8ff' }}>
                <Users size={24} color="#9333ea" />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">List of Users</h3>
                </div>
                <p className="heat-profile-list-description">
                  Manage user accounts, system access, and passwords.
                </p>
              </div>
            </div>

            <div className="heat-profile-header-actions">
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleDownloadData}
                className="heat-profile-filter-btn"
                disabled={!(userData && userData.length > 0)}
              >
                Download As Excel
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showAddEditUserModal('', false)}
                className="heat-profile-add-btn"
              >
                Create New User
              </Button>
            </div>
          </div>

          <div className="heat-profile-table-wrap" style={{ marginTop: '0px' }}>
            <Table
              className="heat-profile-table"
              columns={columns}
              dataSource={filteredData}
              loading={showLoader}
              rowKey="user_id"
              scroll={{ x: 'max-content' }}
              pagination={{
                current: currentPage,
                pageSize: 10,
                total: filteredData.length,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (page) => setCurrentPage(page),
              }}
            />
          </div>
        </div>
      </div>

      {userModalVisible ? (
        <AddUserModal
          setUserModalVisible={setUserModalVisible}
          setUserModalLoading={setUserModalLoading}
          userModalVisible={userModalVisible}
          userForm={userForm}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          onSave={saveUser}
          setCreateUserLoader={setCreateUserLoader}
          createUserLoader={createUserLoader}
          userGroup={userGroup}
          setUserGroup={setUserGroup}
          setUserName={setUserName}
          userName={userName}
          OnRemoveuserFromGroup={onRemoveuserFromGroup}
          OnAdduserToGroup={onAdduserToGroup}
        />
      ) : null}
      {resetPasswordModalVisible ? (
        <ResetPasswordModal
          setResetPasswordModalVisible={setResetPasswordModalVisible}
          resetPasswordModalVisible={resetPasswordModalVisible}
          setResetPasswordModalLoading={(value) => setResetPasswordModalLoading(value)}
          resetPasswordModalLoading={resetPasswordModalLoading}
          setResetPasswordRecord={setResetPasswordRecord}
          resetPasswordRecord={resetPasswordRecord}
        />
      ) : null}
      <ConfirmationModal
        className="user-mgmt-crc-delete-confirm"
        isOpen={deleteUserModalOpen}
        title="Remove this user?"
        message={
          userToDelete
            ? `This action will permanently remove ${userToDelete.name || userToDelete.email}. This cannot be undone.`
            : ''
        }
        confirmText={deleteUserLoading ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleConfirmDeleteUser}
        onCancel={closeDeleteUserModal}
        size="medium"
        confirmButtonProps={{
          disabled: deleteUserLoading,
          backgroundColor: '#00C9A7',
          textColor: '#FFFFFF',
        }}
        cancelButtonProps={{
          disabled: deleteUserLoading,
          backgroundColor: '#FFFFFF',
          textColor: '#374151',
        }}
      />
    </>
  );
};

export default connect(null, null)(UserManagementAGGrid);
